import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useApiClient } from '../../../hooks/useRestAPIHooks';
import {
  Folder,
  ModelUtils,
  rootFolder,
  TemplatingItemSummary,
} from '../models';
import { getTemplatingItemsInFolder } from './getTemplatingItemsInFolder';

const EMPTY_ITEMS: TemplatingItemSummary[] = [];

/** React Query key for recursive folder listing (search / favorites). */
export const ALL_TEMPLATING_ITEM_SUMMARIES_QUERY_KEY = [
  'AITemplates',
  'allTemplatingItemSummaries',
] as const;

export const useAllTemplatingItemSummaries = ({
  enabled = true,
  folder = rootFolder,
  refetchTrigger,
}: {
  enabled?: boolean;
  folder?: Folder;
  refetchTrigger?: unknown;
} = {}) => {
  const apiClient = useApiClient();
  const normalizedFolder = useMemo(
    () => ModelUtils.createFolder(folder.path),
    [folder.path],
  );

  const query = useQuery({
    queryKey: [
      ...ALL_TEMPLATING_ITEM_SUMMARIES_QUERY_KEY,
      normalizedFolder.path,
      refetchTrigger,
    ],
    queryFn: () => getTemplatingItemsInFolder(apiClient, normalizedFolder),
    enabled,
    select: templatingItems => {
      const uniqueTemplatingItems = new Map<string, TemplatingItemSummary>();
      for (const templatingItem of templatingItems) {
        uniqueTemplatingItems.set(templatingItem.id, templatingItem);
      }
      return [...uniqueTemplatingItems.values()];
    },
  });

  return {
    items: query.data ?? EMPTY_ITEMS,
    // When `enabled` is false, react-query can still report loading; callers expect idle.
    isLoading: enabled && query.isLoading,
  };
};
